/**
 * components/object/ObjectQuickActions.jsx — Iter 47 add-note / open-
 * issue / change-status controls. Sits beside the timeline. Each form
 * is intentionally minimal — per directive "Tap object. Add note. Done."
 *
 * Used by the unified ObjectInspector and can be embedded anywhere the
 * field user needs to drop operational memory on an object.
 */
import React, { useEffect, useState, useCallback } from "react";
import { AlertCircle, Send, Loader2, Activity } from "lucide-react";
import {
  addNote, openIssue, transitionIssue, updateStatus, fetchIssues,
  fetchStatus, ISSUE_TRANSITIONS, ISSUE_STATUS_LABEL, OBJECT_STATUS_LABEL,
} from "@/lib/objectIntel";

// ── Note adder ────────────────────────────────────────────────────────

export function ObjectNoteAdder({ objectType, objectId, onAdded }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setErr(null);
    try {
      await addNote(objectType, objectId, t);
      setText("");
      onAdded && onAdded();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to add note");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="object-note-adder">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a note…  (operational memory — no metadata required)"
        rows={2}
        maxLength={2000}
        disabled={busy}
        className="w-full text-sm leading-snug resize-none"
        data-testid="object-note-text"
      />
      <div className="flex items-center gap-2">
        <button
          className="btn btn-primary text-xs"
          onClick={submit}
          disabled={busy || !text.trim()}
          data-testid="object-note-submit"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Add Note
        </button>
        {err && <span className="text-[11px] text-[var(--fop-danger)]">{err}</span>}
      </div>
    </div>
  );
}

// ── Issue list + transitions + open new ────────────────────────────────

export function ObjectIssuesPanel({ objectType, objectId, refreshKey = 0,
                                     onChanged }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setIssues(await fetchIssues(objectType, objectId));
    } finally {
      setLoading(false);
    }
  }, [objectType, objectId]);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  const create = async () => {
    if (!draft.title.trim()) return;
    setBusy(true);
    try {
      await openIssue(objectType, objectId, {
        title: draft.title.trim(),
        description: draft.description.trim() || null,
      });
      setDraft({ title: "", description: "" });
      setShowCreate(false);
      await reload();
      onChanged && onChanged();
    } finally {
      setBusy(false);
    }
  };

  const flip = async (issue, toStatus) => {
    setBusy(true);
    try {
      await transitionIssue(objectType, objectId, issue.id, toStatus);
      await reload();
      onChanged && onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="object-issues-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)]">
          Issues ({issues.length})
        </div>
        {!showCreate && (
          <button
            className="btn btn-ghost text-xs"
            onClick={() => setShowCreate(true)}
            data-testid="object-issue-open-form"
          >
            <AlertCircle size={12} /> Open Issue
          </button>
        )}
      </div>

      {showCreate && (
        <div className="fop-surface p-3 rounded-md space-y-2" data-testid="object-issue-create-form">
          <input
            value={draft.title}
            placeholder="Issue title (e.g. Riser low — verify before paving)"
            maxLength={200}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="w-full text-sm"
            autoFocus
            data-testid="object-issue-title"
          />
          <textarea
            value={draft.description}
            placeholder="Optional context…"
            rows={2}
            maxLength={2000}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            className="w-full text-sm leading-snug resize-none"
            data-testid="object-issue-description"
          />
          <div className="flex gap-2">
            <button
              className="btn btn-primary text-xs"
              onClick={create}
              disabled={busy || !draft.title.trim()}
              data-testid="object-issue-submit"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Open
            </button>
            <button
              className="btn btn-ghost text-xs"
              onClick={() => { setShowCreate(false); setDraft({ title: "", description: "" }); }}
              data-testid="object-issue-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-[var(--fop-text-3)] text-xs flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Loading issues…
        </div>
      )}

      {!loading && issues.length === 0 && (
        <div className="text-[var(--fop-text-3)] text-xs">No issues. Open one when the field needs verification or attention.</div>
      )}

      <ul className="space-y-2">
        {issues.map((iss) => (
          <li key={iss.id} className="fop-surface p-3 rounded-md" data-testid={`object-issue-row-${iss.id}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[var(--fop-text)] font-medium leading-snug">{iss.title}</div>
                {iss.description && (
                  <div className="text-xs text-[var(--fop-text-2)] mt-0.5 leading-snug whitespace-pre-wrap line-clamp-3">{iss.description}</div>
                )}
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] mt-1">
                  {ISSUE_STATUS_LABEL[iss.status] || iss.status}
                  {iss.reopened_count > 0 && ` · reopened ${iss.reopened_count}×`}
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {(ISSUE_TRANSITIONS[iss.status] || []).map((next) => (
                  <button
                    key={next}
                    className="btn btn-ghost text-[11px] px-2 py-1"
                    onClick={() => flip(iss, next)}
                    disabled={busy}
                    data-testid={`object-issue-${iss.id}-to-${next}`}
                  >
                    {ISSUE_STATUS_LABEL[next] || next}
                  </button>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Object status picker ───────────────────────────────────────────────

export function ObjectStatusPicker({ objectType, objectId, onChanged }) {
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const s = await fetchStatus(objectType, objectId);
      setCurrent(s?.status || null);
    })();
  }, [objectType, objectId]);

  const choose = async (status) => {
    setBusy(true);
    try {
      await updateStatus(objectType, objectId, status);
      setCurrent(status);
      onChanged && onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fop-surface p-3 rounded-md" data-testid="object-status-picker">
      <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] mb-2">
        Object Status
      </div>
      <div className="grid grid-cols-2 gap-1">
        {Object.entries(OBJECT_STATUS_LABEL)
          .filter(([val]) => val !== "open")  // legacy alias; hide
          .map(([val, label]) => {
          const active = current === val
            || (val === "not_started" && current === "open");
          return (
            <button
              key={val}
              disabled={busy}
              onClick={() => choose(val)}
              className={`text-[11px] px-2 py-1.5 rounded-md border transition-colors ${
                active
                  ? "bg-[var(--fop-primary)] text-[var(--fop-bg)] border-[var(--fop-primary)]"
                  : "bg-[var(--fop-surface)] text-[var(--fop-text-2)] border-[var(--fop-border)] hover:text-[var(--fop-text)]"
              }`}
              data-testid={`object-status-${val}`}
            >
              <Activity size={10} className="inline mr-1" /> {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
